<template>
    <div class="flex-grow-1 d-flex flex-column">
        <div class="grid-count mb-3 d-flex">
            <assessment-type-select></assessment-type-select>
            <div class="spacer"></div>
            <span class="align-self-end">
                {{ count }} Actions
                <span class="font-weight-bold"
                      v-if="selectedCount > 0">
                    | {{ selectedCount }} selected
                </span>
            </span>
            <div class="ml-auto d-flex align-content-center">
                <b-dropdown
                        no-caret
                        right
                        variant="grey"
                        toggle-class="py-1 px-2"
                        class="mr-2 mr-sm-3"
                >
                    <template #button-content>
                        Actions
                        <font-awesome-icon
                                class="ml-1"
                                :icon="['fal', 'chevron-down']"
                                size="xs"
                        >
                        </font-awesome-icon>
                    </template>
                    <b-dropdown-item
                            @click="onClickDownloadCsv"
                    >
                        <font-awesome-icon
                                :icon="['fal', 'download']"
                                size="sm"
                        ></font-awesome-icon>
                        Download CSV
                    </b-dropdown-item>
                </b-dropdown>
                <reset-filters
                        @click="resetGridToDefaultOptions"></reset-filters>
                <refresh-button
                        @click="refresh"
                        :spinning="refreshing"
                >
                </refresh-button>
            </div>
        </div>
        <div class="flex-grow-1 d-flex">
            <ag-grid-vue
                    :style="'width: ' + gridWidth +'%;'"
                    class="ag-theme-balham"

                    :gridOptions="gridOptions"
                    :defaultColDef="defaultColDef"
                    :columnDefs="columnDefs"
                    :columnTypes="columnTypes"
                    :rowData="rowData"

                    @gridReady="onGridReady"
                    :floatingFilter="floatingFilter"
                    :cacheQuickFilter="cacheQuickFilter"
                    :side-bar="sideBarDef"

                    :suppressExcelExport="suppressExcelExport"
                    :suppressContextMenu="true"

                    :isExternalFilterPresent="isExternalFilterActive"
                    :doesExternalFilterPass="doesExternalFilterPass"

                    @cellClicked="onCellClicked"
                    :headerHeight="rowHeight"
                    :rowHeight="rowHeight"

                    @columnMoved="saveGridOptions"
                    @columnPinned="saveGridOptions"
                    @columnResized="saveGridOptions"
                    @columnVisible="saveGridOptions"
                    @filterChanged="saveGridOptions"
                    @sortChanged="saveGridOptions"
            >
            </ag-grid-vue>
        </div>
        <action-modal
            v-if="modals.action.visible"
            :show.sync="modals.action.visible"
            :client-action-id="clientActionId"
        >
        </action-modal>
    </div>
</template>

<script>
import {
  actionGridDefaultColDef as gridDefaultColDef,
  actionGridColumnTypes as columnTypes,
} from '@/services/Action/ActionGridConfig';
import { mapGetters } from 'vuex';
import { gridColumns } from '@/services/Property/PropertyActionGrid';
import GridMixin from '@/components/Grid/GridMixin';
import Property from '@/services/Property/Property';
import dayjs from 'dayjs';
import ActionModal from '@/components/Action/Modal/ActionModal';
import RefreshButton from '@/components/Grid/RefreshButton';
import ResetFilters from '@/components/Grid/ResetFilters';
import AssessmentTypeSelect from '@/components/Nav/AssessmentTypeSelect';
import AddClientActionModal from '@/components/Action/Modal/AddClientActionModal';
import riskhub from '@/services/Riskhub/Riskhub';

export default {
  name: 'Property-Action-Grid',
  components: {
    AddClientActionModal,
    ActionModal,
    RefreshButton,
    ResetFilters,
    AssessmentTypeSelect,
  },
  mixins: [GridMixin],
  props: {
    property: {
      required: true,
      type: Object,
    },
    shown: {
      type: Boolean,
    },
  },
  data() {
    return {
      rowData: [],
      defaultColDef: gridDefaultColDef,
      columnDefs: null,
      columnTypes,
      clientAction: null,
      clientActionId: null,
      modals: {
        action: {
          visible: false,
        },
        add: {
          visible: false,
        },
      },
      gridName: 'property-action-grid',
    };
  },
  created() {
    this.columnDefs = gridColumns.filter(this.filterColumn);
  },
  computed: {
    ...mapGetters({
      actionRefresh: 'actionRefresh',
    }),
  },
  methods: {
    postGridReady() {
      this.setRowClasses();

      this.refresh();
    },

    /**
     * Filter columns based on permissions
     *
     * @param {Object} column
     */
    filterColumn(column) {
      return !(column.field === 'dueDate' && !this.$can(
        this.$perm.ACTIONS_VIEW_ACTION_DUE_DATE,
        this.$perm.PERMISSION_TYPE_ACTION,
      ));
    },

    async refresh() {
      this.refreshing = true;
      const { data } = await Property.getActions(
        this.property.propertyId,
        { complianceTypeId: this.$store.getters.complianceTypeId }
      );

      this.rowData = data.data;

      await this.setGridOptions();
      this.refreshing = false;
    },

    /**
     * Set row classes
     */
    setRowClasses() {
      const now = dayjs();
      const threeMonths = dayjs().add(3, 'month');

      this.gridOptions.getRowClass = ({ data }) => {
        if (!data) {
          return '';
        }
        return '';
      };
    },

    onCellClicked({ data }) {
      this.clientActionId = data.clientActionId;
      this.modals.action.visible = true;
    },

    doesExternalFilterPass(node) {
      return true;
    },

    isExternalFilterActive() {
      return true;
    },

    addClientAction() {
      const selectedRows = this.getSelectedNodes();

      if (selectedRows.length > 1) {
        this.$notify({
          title: 'Multiple actions selected',
          text: 'When adding a new client action, please select a maximum of one action',
          type: 'error',
        });

        return;
      }

      this.clientAction = null;

      const selectedRow = selectedRows[0];
      if (selectedRow) {
        this.clientAction = selectedRow.data;
      }

      this.openModal('add');
    },

    /**
     * Opens a modal by name (in data -> modals.[name])
     */
    openModal(name) {
      if (this.refreshing) {
        this.$notify({
          title: 'Please wait..',
          text: 'Actions are currently being loaded, please try again',
          type: 'warn',
        });
        return false;
      }

      this.modals[name].visible = true;
      return true;
    },

    onClickDownloadCsv() {
      const processCellCallback = (params) => {
        const column = params.column.getColId();
        let { value } = params;

        switch (column) {
          case 'dueDate':
          case 'internalDueDate':
          case 'extendedDueDate':
          case 'dateReferredToLegal':
            value = riskhub.formatDate(value, true) || 'N/A';
            break;
        }

        return value;
      };

      const onlySelected = this.getSelectedNodes().length > 0;
      const params = {
        processCellCallback,
        onlySelected,
      };

      this.gridApi.exportDataAsCsv(params);
    },
  },
  watch: {
    shown() {
      this.gridApi.sizeColumnsToFit();
    },
    '$store.getters.complianceTypeId': function() {
      this.refresh();
    },
    actionRefresh(refresh) {
      if (refresh) {
        this.refresh();
        this.$store.dispatch('refreshActionsComplete');
      }
    },
  },
};
</script>

<style lang="scss">
    .compliance-type-select {
        padding: 0 !important;

        .multiselect_ {
            &_single {
                .option__title {
                    line-height: 44px;
                }
            }

            &_caret {
                line-height: 28px;
            }
        }
    }

    .spacer {
        margin: 0 22px 0 17px;
    }
</style>
